import { createContext, useContext } from 'react';
import type { PresetId } from '../qc/customPresets';
import type { BatchFileInput, BatchFileResult, BatchJob, BatchTarget } from './types';

/**
 * Batch state shared above the routes: the queue, results, and run snapshot
 * live in a provider mounted in the AppShell instead of route-local state, so
 * an in-flight run keeps its worker alive independent of the Convert view.
 * (RouteView now keeps both modes mounted, cue-forge-drt.14.) Kept
 * component-free so the provider file can satisfy react-refresh's
 * components-only rule.
 */
export interface BatchValue {
  /** Files queued for the next run. */
  files: BatchFileInput[];
  setFiles: (files: BatchFileInput[]) => void;
  /** Job form state (live — editable between runs). */
  target: BatchTarget;
  setTarget: (target: BatchTarget) => void;
  shift: string;
  setShift: (shift: string) => void;
  /** A {@link PresetId} (built-in name or `custom:${uuid}` id); `''` means no QC. */
  preset: PresetId;
  setPreset: (preset: PresetId) => void;
  autoFix: boolean;
  setAutoFix: (autoFix: boolean) => void;
  /** Per-file ingestion notices (overflow, size-cap rejections). */
  notices: string[];
  setNotices: (notices: string[]) => void;
  /** Whether a run is in flight. */
  running: boolean;
  setRunning: (running: boolean) => void;
  progress: { done: number; total: number } | null;
  setProgress: (progress: { done: number; total: number } | null) => void;
  results: BatchFileResult[];
  setResults: (results: BatchFileResult[]) => void;
  /** Snapshot of the job the current results were produced with. */
  runJob: BatchJob | null;
  setRunJob: (job: BatchJob | null) => void;
  /** Fatal run failure (e.g. the worker crashed), else null. */
  runError: string | null;
  setRunError: (error: string | null) => void;
}

export const BatchContext = createContext<BatchValue | null>(null);

/** Access shared batch state. Must be used within a BatchProvider. */
export function useBatch(): BatchValue {
  const ctx = useContext(BatchContext);
  if (!ctx) throw new Error('useBatch must be used within a BatchProvider');
  return ctx;
}
